import { Injectable } from '@angular/core';
import { AlgoliaEntity } from '../enums/algolia-entity';
import { SearchResultListItem } from '../models/search-result-list-item';
import { AlgoliaRecordHit } from '../models/algolia-record-hit';
import { BallotType } from '../../../../shared/enums/ballot-type-enum';
import { IConnectEnumValue } from '../../../../shared/models/connect-enum';
import { ConfigurationService } from '../../../../shared/services/configuration.service';

@Injectable({
  providedIn: 'root'
})
export class AlgoliaRecordHitService {
  algoliaEntityEnumDto: IConnectEnumValue<AlgoliaEntity>[] = [];

  constructor(private configurationService: ConfigurationService) {
    this.loadAlgoliaRecordEntityEnumDto();
  }


  public loadAlgoliaRecordEntityEnumDto(): void {
    this.configurationService.getAlgoliaEntityEnumDto().subscribe({
      next: (response) => {
        this.algoliaEntityEnumDto = response;
      },
      error: (error) => {
        console.error('Error loading Algolia Record Entity Enum DTO:', error);
      }
    });
  }

  getSearchResultItemLink(hit: AlgoliaRecordHit): string {
    let result: string = '';

    switch(hit.entityType) {
        case AlgoliaEntity.Ballot:

          let ballotTypeRouteSegment = '';

          switch(hit.ballotTypeId) {
            case BallotType.CIB:
              ballotTypeRouteSegment = 'cib';
              break;
            case BallotType.IEC:
              ballotTypeRouteSegment = 'iec';
              break;
            case BallotType.NPB:
              ballotTypeRouteSegment = 'npb';
              break;
            case BallotType.SR:
              ballotTypeRouteSegment = 'srs';
              break;
            default:
              ballotTypeRouteSegment = '';
          }
          result = `/ballots/${ballotTypeRouteSegment}/${hit.ballotId}`;
          break;
        case AlgoliaEntity.Project:
          result = `/projects/${hit.projectId}`;
          break;
        case AlgoliaEntity.Committee:
          result = `/committees/${hit.committeeCodeForDisplay}`;
          break;
        case AlgoliaEntity.Publication:
          result = `/sa-library/${hit.publicationUri}`;
          break;
        default:
          // For other entity types, you can implement additional mapping functions or handle them as needed
          console.warn(`getSearchResultItemLink: No mapping implemented for entity type: ${hit.entityType}`);
      }
      return result;
  }

  getListItem(hit: AlgoliaRecordHit) : SearchResultListItem | null {
    let listItem: SearchResultListItem | null = null;

    switch(hit.entityType) {
        case AlgoliaEntity.Ballot:
          listItem = this.mapBallotHitToListItem(hit);
          break;
        case AlgoliaEntity.Project:
          listItem = this.mapProjectHitToListItem(hit);
          break;
        case AlgoliaEntity.Committee:
          listItem = this.mapCommitteeHitToListItem(hit);
          break;
        case AlgoliaEntity.Publication:
          listItem = this.mapPublicationHitToListItem(hit);
          break;
        default:
          // For other entity types, you can implement additional mapping functions or handle them as needed
          console.warn(`getListItem: No mapping implemented for entity type: ${hit.entityType}`);
      }

      return listItem;
  }

  getBallotHitListItems(hits: AlgoliaRecordHit[]) : SearchResultListItem[] {

    const ballotHits = hits
      .filter(hit => hit.entityType === AlgoliaEntity.Ballot)
      .map(hit => this.mapBallotHitToListItem(hit));

    return ballotHits;

  }

  mapBallotHitToListItem(hit: AlgoliaRecordHit) : SearchResultListItem {
    const result : SearchResultListItem = {
      entityType: hit.entityType,
      entityTypeDisplayText: hit.facetEntityType && hit.facetEntityType.length > 0 ? hit.facetEntityType[0] : '',
      id: hit.objectID,
      title: hit.ballotReferenceForDisplay?? '',
      subTitle: hit.ballotTitleForDisplay?? '',
      description: hit.ballotCommitteeCodeForDisplay?? '',
    };

    return result;
  }

  getProjectHitListItems(hits: AlgoliaRecordHit[]) : SearchResultListItem[] {

    const projectHits = hits
      .filter(hit => hit.entityType === AlgoliaEntity.Project)
      .map(hit => this.mapProjectHitToListItem(hit));
    return projectHits;
  }

  mapProjectHitToListItem(hit: AlgoliaRecordHit): SearchResultListItem {
    const result : SearchResultListItem = {
      entityType: hit.entityType,
      entityTypeDisplayText: hit.facetEntityType && hit.facetEntityType.length > 0 ? hit.facetEntityType[0] : '',
      id: hit.objectID,
      title: hit.projectDesignationForDisplay?? '',
      subTitle: hit.projectTitleForDisplay?? '',
      description: hit.projectCommitteeCodeForDisplay?? '',
    };

    return result;
  }

  mapCommitteeHitToListItem(hit: AlgoliaRecordHit) : SearchResultListItem {
    const result : SearchResultListItem = {
      entityType: hit.entityType,
      entityTypeDisplayText: hit.facetEntityType && hit.facetEntityType.length > 0 ? hit.facetEntityType[0] : '',
      id: hit.objectID,
      title: hit.committeeTitleForDisplay?? '',
      subTitle: hit.committeeCodeForDisplay?? '',
      description: hit.committeeSectorForDisplay?? '',
    };

    return result;
  }

  mapPublicationHitToListItem(hit: AlgoliaRecordHit) : SearchResultListItem {
    const result : SearchResultListItem = {
      entityType: hit.entityType,
      entityTypeDisplayText: hit.facetEntityType && hit.facetEntityType.length > 0 ? hit.facetEntityType[0] : '',
      id: hit.objectID,
      title: hit.publicationDatedDesignationForDisplay?? '',
      subTitle: hit.publicationFullTitleForDisplay?? '',
      description: hit.publicationCommitteeCodeForDisplay?? '',
    };

    return result;
  }
}
